import { Component, OnDestroy, OnInit, SecurityContext } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Location } from '@angular/common';
import { filter } from 'rxjs/operators';
import { PghUserDeviceBase } from 'src/app/shared/device/pgh-user-device';
import { MatDialog } from '@angular/material/dialog';
import { UserClaims } from 'src/app/model/okta/userClaims';
import { GoogleAnalyticsService } from 'src/app/core/services/google-tag-manager.service';
import { ChatHandler } from 'src/app/core/handler/chat-handler';
import { combineLatest, Subject } from 'rxjs';
import { DeviceType } from 'src/app/shared/device/device-type/device-type';
import { DeviceTypeService } from 'src/app/shared/device/device-type/device-type.service';
import { SelectedDeviceService } from 'src/app/shared/device/selected-device.service';
import { ISelectedContract, LoggedInUserSettings } from 'src/app/core/services/settings/logged-in-user-settings';
import { ActivatedRoute } from '@angular/router';
import { DeviceTypeDto } from 'src/app/core/services/device-inventory/device-type.dto';
import { DeviceInventoryService } from 'src/app/core/services/device-inventory.service';
import { IRegisteredDevice } from 'src/app/modules/account/components/claims-flow/claims-flow.component';
import { GSSPAuthenticationService } from 'src/app/core/services/gssp-authentication.service';
import { ADPResourceService } from 'digital-core';
import { UtilityService } from 'src/app/core/services/utility.service';
import { el } from 'date-fns/locale';
import { IFaqListItemKeys } from 'src/app/model/sitefinity/IFaq';
import { CHAT, CHATGROUP } from 'src/app/core/constants/feature-key.constants';
import { AccountService } from 'src/app/core/services/account.service';
import { InventoryInfoDto } from 'src/app/core/services/device-inventory/inventory-info.dto';
import { GTMClaimType } from '../../../../model/google-tag-manager/api-status';
import { DomSanitizer } from '@angular/platform-browser';
import { GlobalModalWindowService } from 'src/app/core/services/global-modal-window.service';
import { AsappChatService } from '../../../../core/services/asapp-chat.service';

@Component({
  selector: 'pgh-claims-support',
  templateUrl: './claims-support.component.html',
  styleUrls: ['./claims-support.component.scss'],
})
export class ClaimsSupportComponent implements OnInit, OnDestroy {
  public headerText?: string;
  public selDeviceNickname: string;
  public selDeviceName: string;
  public contractId: string = '';
  public selDevice: PghUserDeviceBase;
  public isUserPrimary: boolean = false;
  public deviceType?: DeviceType;
  public coverageBenefitsReimburementsEnabled: boolean = false;
  public hasAccidentalDamageCoverage: boolean = false;
  public hasMechanicalBreakdownCoverage: boolean = false;
  public isTechServicesChatEnabled: boolean = false;
  public techSupportList: string[] = [];
  public techCoveredSupportList: string[] = [];
  public techNonCoveredSupportList: string[] = [];
  public fileClaimAccidentalDamageCoverageList: string[] = [];
  public fileClaimInsuranceCoverageList: string[] = [];
  public fileClaimEnabled: boolean = false;
  public showFileClaim: boolean = false;
  public waitingPeriod: number;
  public withinWaitingPeriod: boolean = false;
  public noCoverageSecondary: boolean = false;
  public noCoverageDefault: boolean = false;
  public contractStartDate: Date;
  public manageSubscriptionAriaLabel: string;
  public primaryUserFAQListItems: IFaqListItemKeys[] = [];
  public secondaryUserFAQListItems: IFaqListItemKeys[] = [];
  public primaryUserFAQ: string[] = [];
  public secondaryUserFAQ: string[] = [];
  public isNative: boolean;
  chatOffHoursStartTimeInUTC = '4';
  chatOffHoursEndTimeInUTC = '12';
  isActive: any;
  isSuspended: boolean = false;
  allowFileClaimIfSuspended: boolean = false;
  isGSSP: boolean;
  masterCertificateNumber: string;
  childCertificateNumber: any;
  dealerCode: string;
  coverageTypeCode: string;
  contractNum: string;
  redirectUrl: string;
  zipCode: string;
  domSanitizer: any;
  isSPOEEnabled: boolean  = false;
  isChatAvailable: boolean = false;
  registeredDevice = {} as IRegisteredDevice;
  private inventoryInfos?: InventoryInfoDto[] = [];
  private onDestroy$ = new Subject<void>();
  fileClaimUrl: string;
  protected readonly GTMClaimType = GTMClaimType;

  readonly OKTA_TOKEN_STORAGE = 'okta-token-storage';

  constructor(
    private utilityService: UtilityService,
    private selectedDeviceService: SelectedDeviceService,
    private analytics: GoogleAnalyticsService,
    private location: Location,
    private dialog: MatDialog,
    private loggedInUserSettings: LoggedInUserSettings,
    private activatedRoute: ActivatedRoute,
    public httpClient: HttpClient,
    private deviceTypeService: DeviceTypeService,
    private deviceInventoryService: DeviceInventoryService,
    private gsspAuthService: GSSPAuthenticationService,
    private resourceService: ADPResourceService,
    private accountService: AccountService,
    private sanitizer: DomSanitizer,
    private globalModalWindowService: GlobalModalWindowService,
    private asappChatService: AsappChatService
  ) {}

  ngOnInit() {
    this.isSPOEEnabled = this.resourceService.isPortalSettingTrue('Global_SPOE_Enabled');
    this.activatedRoute.queryParams.subscribe((params) => {
      this.isNative = (params.isNative && params.isNative.toLowerCase().trim() === 'true') || this.utilityService.isNativeApp;
    });
    const joined$ = combineLatest([this.loggedInUserSettings.$selectedContract, this.loggedInUserSettings.$userClaims]).pipe(
      filter(([a, b]) => a !== null && b !== null)
    );

    joined$.subscribe(([contract, userClaims]: [ISelectedContract, UserClaims]) => {
      this.contractId = contract.id;
      this.isSuspended = contract.isSuspended;
      this.isUserPrimary = this.analytics.isUserPrimary(contract, userClaims);
      this.zipCode = contract?.zipCode;
      this.isGSSP = contract.useGSSPClaims;
      this.contractStartDate = new Date(contract.startDate);
      this.selDevice = this.selectedDeviceService.getSelectedDevice();
      this.fileClaimEnabled = this.resourceService.isPortalSettingTrue('ClaimsSupportCenter_FileClaim_Enabled');
      this.techCoveredSupportList = this.resourceService
        .getPortalSetting('ClaimsSupportCenter_CoveredDevice_TechSupport_List')
        .Value.split(';');
      this.techNonCoveredSupportList = this.resourceService
        .getPortalSetting('ClaimsSupportCenter_NonCoveredDevice_TechSupport_List')
        .Value.split(';');
      this.waitingPeriod = parseInt(this.resourceService.getPortalSetting('ClaimsSupportCenter_FileClaim_WaitingPeriod').Value);
      this.fileClaimAccidentalDamageCoverageList = this.resourceService
        .getPortalSetting('ClaimsSupportCenter_FileClaim_AccidentalDamage_Coverage_List')
        .Value.split(';');
      this.coverageBenefitsReimburementsEnabled = this.resourceService.isPortalSettingTrue('Global_DeviceDetails_Benefits_Enabled');
      this.manageSubscriptionAriaLabel = this.resourceService.getPortalLabel(
        'ClaimsSupportCenter_Card_Subscription_Button_AriaLabel'
      )?.Value;
      this.allowFileClaimIfSuspended = this.resourceService.isPortalSettingTrue('Global_SuspendedSubscription_FileClaimAllowed');
      this.setTechSupportList();
      this.setClaimsSection();
      this.isTechServicesChatEnabled =
        this.loggedInUserSettings.isFeatureEnabled(contract.dealerId, CHATGROUP) &&
        this.loggedInUserSettings.isFeatureEnabled(contract.dealerId, CHAT);
      this.headerText = this.activatedRoute?.snapshot?.data?.headerText ?? '';
      this.getFAQListItems();
      if (this.selDevice?.deviceSource != 'EPRISM') {
        this.deviceInventoryService.getDeviceTypes(this.selDevice?.inventoryId).subscribe({
          next: (deviceTypes) => {
            this.getSelectedDeviceDetails(deviceTypes);
          },
        });
      } else {
        this.getSelectedDeviceDetails([]);
      }
      this.accountService.getAllInventoriesWithDevicesForSelectedContract(this.onDestroy$).subscribe({
        next: (inventories) => {
          this.inventoryInfos.push(inventories.cellularEnabledPolicyInventory?.info ?? null);
          this.inventoryInfos.push(inventories.gadgetClientSoldPolicyInventory?.info ?? null);
          this.inventoryInfos.push(inventories.gadgetPolicyInventory?.info ?? null);
        },
        error: (error) => {
          throw error;
        },
      });
    });

    this.getLiveChatAvailability();

    //navigating from device select page retains vertical scroll position, need to scroll to top on entry to claims & support page
    window.scrollTo(0, 0);

    this.fileClaimUrl = this.resourceService.getPortalSetting('URL_ClaimsPortal').Value;
  }

  getFAQListItems() {
    let questionIndex: number = 0;
    let answerIndex: number = 0;
    let faqList: string[] = [];
    const manualListArray = new Array();

    if (this.isUserPrimary) {
      faqList = this.resourceService.getPortalSetting('ClaimsSupportCenter_FAQ_Primary_User').Value.split(';');
    } else {
      faqList = this.resourceService.getPortalSetting('ClaimsSupportCenter_FAQ_Secondary_User').Value.split(';');
    }

    if (faqList) {
      faqList.forEach((faqItem) => {
        if (faqItem) {
          if (faqItem.includes('Question')) {
            manualListArray.push([]);
            manualListArray[questionIndex].questionKey = faqItem;
          } else if (faqItem.includes('Answer')) {
            manualListArray[questionIndex].answerKey = faqItem;
            questionIndex++;
          }
          answerIndex++;
        }
      });

      if (this.isUserPrimary) {
        this.primaryUserFAQListItems = manualListArray;
      } else {
        this.secondaryUserFAQListItems = manualListArray;
      }
    }
  }

  getSelectedDeviceDetails(deviceTypes: DeviceTypeDto[]) {
    this.deviceType = this.deviceTypeService.getDeviceTypeByCodeOrDefault(this.selDevice?.deviceTypeCode, {
      displayName: this.selDevice?.deviceTypeName,
    });

    this.selDeviceName = this.selDevice?.make + ' ' + this.selDevice?.model;
    this.selDeviceNickname = this.selDevice?.nickName;

    if (this.selDevice.coverage != null) {
      this.hasMechanicalBreakdownCoverage = this.selDevice?.coverage?.hasMechanicalDamage ?? false;
      this.hasAccidentalDamageCoverage = this.selDevice?.coverage?.hasAccidentalDamage ?? false;
    } else {
      const foundType = deviceTypes.find((dt) => dt.code === this.selDevice?.deviceTypeCode);
      this.hasMechanicalBreakdownCoverage = foundType?.coverages?.hasMechanicalDamage ?? false;
      this.hasAccidentalDamageCoverage = foundType?.coverages?.hasAccidentalDamage ?? false;
    }
  }
  setTechSupportList() {
    let hasCoverage =
      this.selDevice.coverage != null && (this.selDevice.coverage.hasAccidentalDamage || this.selDevice.coverage.hasMechanicalDamage);
    if (hasCoverage) {
      this.techSupportList = this.techCoveredSupportList;
    } else {
      this.techSupportList = this.techNonCoveredSupportList;
    }
  }

  setClaimsSection() {
    if (!this.isUserPrimary) {
      this.noCoverageSecondary = true;
    } else {
      if (
        this.selDevice.coverage != null &&
        (this.selDevice.coverage.hasAccidentalDamage || this.selDevice.coverage.hasMechanicalDamage) &&
        this.isGSSP
      ) {
        if (this.waitingPeriod > 0) {
          const claimStartDate = new Date(this.contractStartDate);
          const todaysdate = new Date();
          claimStartDate.setDate(this.contractStartDate.getDate() + this.waitingPeriod);
          if (todaysdate.getTime() < claimStartDate.getTime()) {
            this.withinWaitingPeriod = true;
          } else {
            this.showFileClaim = !this.allowFileClaimIfSuspended && this.isSuspended ? false : true;
          }
        } else {
          this.showFileClaim = !this.allowFileClaimIfSuspended && this.isSuspended ? false : true;
        }
      } else {
        this.noCoverageDefault = true;
      }
    }
  }

  ngOnDestroy() {
    document.documentElement.className = '';
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  handleBack() {
    this.location.back();
  }

  handleLiveChat() {
    // Show chat or not available dialog
    const chatHandler = new ChatHandler(this.dialog, this.resourceService, this.utilityService, this.activatedRoute, this.asappChatService);
    chatHandler.openChatInterface();
  }

  getLiveChatAvailability() {
    this.chatOffHoursStartTimeInUTC = this.resourceService.getPortalSetting('Chat_OffHours_StartTime_UTC24HT')?.Value ?? '4';
    this.chatOffHoursEndTimeInUTC = this.resourceService.getPortalSetting('Chat_OffHours_EndTime_UTC24HT')?.Value ?? '12';
    // check if chat bubble button is active, not being used in lower environments, let the default button click handle it
    // leaving for now for reference if needed later
    const button: HTMLElement = document.querySelector('.helpButtonEnabled');

    if (button) {
      // this only checks if we are within the active hours and set the flag to true or false
      const now = new Date();
      if (now.getUTCHours() >= parseInt(this.chatOffHoursStartTimeInUTC) && now.getUTCHours() < parseInt(this.chatOffHoursEndTimeInUTC)) {
        // if outside hours, chat is offline
        this.isChatAvailable = false;
      } else {
        // chat is online
        this.isChatAvailable = true;
      }
    } else {
      // if chat bubblde is not active, then chat is offline
      this.isChatAvailable = false;
    }
  }

  handleFileClaim() {
    const searchClaimsUrl = this.resourceService.getPortalSetting('URL_ClaimsPortal').Value;
    const joined$ = combineLatest([this.loggedInUserSettings.$selectedContract]).pipe(filter(([a]) => a !== null));

    joined$.subscribe(([selectedContract]: [ISelectedContract]) => {
      // this.isActive = this.contractGuard.activateGuard(selectedContract, false);
      this.isGSSP = selectedContract.useGSSPClaims;
      this.masterCertificateNumber = selectedContract?.id;

      //choose the correct child certificate based on the inventory the selected device is in
      this.childCertificateNumber = this.inventoryInfos.find((info) => info?.id === this.selDevice.inventoryId)?.policyCertificateNumber;

      this.dealerCode = selectedContract.dealerId;
      this.coverageTypeCode = 'AD';
      this.contractNum =
        selectedContract && selectedContract?.members && selectedContract?.members.length
          ? selectedContract?.members[0].contractNumber
          : '';
    });

    if (this.masterCertificateNumber && this.masterCertificateNumber.length > 0) {
      const childCertificateNumbers: [string] = [this.childCertificateNumber];
      this.registeredDevice.registeredItemName = this.selDevice.nickName;
      this.registeredDevice.model = this.selDevice.model;
      this.registeredDevice.deviceTypeCode = this.selDevice.deviceTypeCode;
      this.registeredDevice.modelIdentifier = this.selDevice.modelIdentifier;
      this.registeredDevice.manufacturer = this.selDevice.make;
      this.registeredDevice.purchasePrice =
        this.selDevice.purchasePrice != null && this.selDevice.purchasePrice != '' ? Number(this.selDevice.purchasePrice) : null;
      this.registeredDevice.purchasedDate = this.selDevice.purchaseDate ?? null;
      this.registeredDevice.serialNumber = this.selDevice.serialNumber;

      this.gsspAuthService
        .postGenerateGSSPToken(this.dealerCode, this.masterCertificateNumber, childCertificateNumbers, this.registeredDevice)
        .subscribe({
          next: (gsspAuthToken: any) => {
            if (gsspAuthToken && gsspAuthToken.length > 0) {
              this.redirectUrl = `${searchClaimsUrl}?accessToken=${gsspAuthToken}&redirectUrl=/file-claim`;
              // this.redirectUrl = this.domSanitizer.sanitize(SecurityContext.URL, this.redirectUrl);
              window.open(this.redirectUrl, '_self');
            } else {
              throw new Error('Failed to connect to Claim system');
            }
          },
        });
    }
  }

  public handleViewClaimStatus() {
      const searchClaimsUrl = this.resourceService.getPortalSetting('URL_ClaimsPortal').Value;
      const joined$ = combineLatest([this.loggedInUserSettings.$selectedContract]).pipe(filter(([a]) => a !== null));

      joined$.subscribe(([selectedContract]: [ISelectedContract]) => {
        // this.isActive = this.contractGuard.activateGuard(selectedContract, false);
        this.isGSSP = selectedContract.useGSSPClaims;
        this.masterCertificateNumber = selectedContract?.id;

        //choose the correct child certificate based on the inventory the selected device is in
        this.childCertificateNumber = this.inventoryInfos.find((info) => info?.id === this.selDevice.inventoryId)?.policyCertificateNumber;

        this.dealerCode = selectedContract.dealerId;
        this.coverageTypeCode = 'AD';
        this.contractNum =
          selectedContract && selectedContract?.members && selectedContract?.members.length
            ? selectedContract?.members[0].contractNumber
            : '';
      });

      if (this.isGSSP) {
        if (this.masterCertificateNumber && this.masterCertificateNumber.length > 0) {
          const childCertificateNumbers: [string] = [this.childCertificateNumber];
          this.gsspAuthService.postGenerateGSSPToken(this.dealerCode, this.masterCertificateNumber, childCertificateNumbers, null).subscribe({
            next: (gsspAuthToken: any) => {
              if (gsspAuthToken && gsspAuthToken.length > 0) {
                this.redirectUrl = `${searchClaimsUrl}?accessToken=${gsspAuthToken}`;
                this.redirectUrl = this.sanitizer.sanitize(SecurityContext.URL, this.redirectUrl);
                window.open(this.redirectUrl, '_self');
              } else {
                this.globalModalWindowService.showModalWindowMin(
                  'Global_Modal_Error_InvalidGsspAuthToken_Title',
                  'GetGsspAuthToken',
                  'Modal_Error_Content_ClaimHistory'
                );
              }
            },
          });
        } else {
          this.globalModalWindowService.showModalWindowMin(
            'DeviceDetails_MissingMasterCertificateError_Message',
            'GetGsspAuthToken',
            'Modal_Error_Content_ClaimHistory'
          );
        }
      } else {
        const tokens = JSON.parse(localStorage.getItem(this.OKTA_TOKEN_STORAGE));
        const accessToken =
          tokens.access_token && tokens.access_token.accessToken ? tokens.access_token.accessToken : tokens?.accessToken?.accessToken;
        const hashedQuery = btoa(`cn=${this.contractNum}&zip=${this.zipCode}`);
        if (accessToken.indexOf(' ') >= 0) {
          console.log('invalid token');
        } else {
          this.redirectUrl = `${searchClaimsUrl}?q=${hashedQuery}&token=${accessToken}`;
          this.redirectUrl = this.sanitizer.sanitize(SecurityContext.URL, this.redirectUrl);
          window.open(this.redirectUrl, '_blank');
        }
      }
    }

  handleManageSubscription() {
    window.open(this.resourceService.getPortalSetting('URL_Account_Subscription_Mangement').Value, '_blank');
  }

  getViewLabel(item: string) {
    const label = this.resourceService.getViewLabel(item) ?? '';
    return this.resourceService.transformContentData(label);
  }
}
